import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const UrlDecoderContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Intro */}
      {/* SEO Title & Subtitle */}
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        URL Decoder – Decode Encoded URLs Quickly and Safely
      </Typography>
      <Typography
        variant="h6"
        component="h2"
        color="text.secondary"
        gutterBottom
      >
        Easily convert encoded URLs back to their original human-readable format
      </Typography>

      <Typography variant="body1" paragraph>
        Have an encoded URL with percent signs and special characters? Our URL
        decoder tool instantly converts encoded URLs back into their original
        format so you can read, edit, or share them easily.
      </Typography>
      <Typography variant="body1" paragraph>
        Whether you're debugging web applications or just want to view the real
        URL, our decoder makes the process simple and fast.
      </Typography>

      {/* Features */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Why Use Our URL Decoder Online Tool?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block" }}>
        <li>🔓 Decode any encoded URL safely and correctly</li>
        <li>⚡ Fast decoding for URLs of any length</li>
        <li>🔍 Useful for debugging and analyzing web requests</li>
        <li>📥 Copy decoded URLs easily or download them for sharing</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Use the URL Decoder
      </Typography>
      <ol style={{ textAlign: "left", display: "inline-block" }}>
        <li>Paste or enter your encoded URL into the input field.</li>
        <li>
          Click “Decode URL” to transform it into its original readable format.
        </li>
        <li>Copy the decoded URL or use it as needed.</li>
      </ol>

      {/* SEO Paragraph */}
      <Typography variant="body1" paragraph mt={4}>
        Our <b>URL decoder</b> is widely used by developers, testers, and
        digital marketers who need to understand or modify URLs that contain
        encoded characters. It accurately reverses the encoding so you can work
        with clean URLs.
      </Typography>

      <Typography variant="body1" paragraph>
        Avoid confusion and errors caused by encoded URLs—use our{" "}
        <b>URL decoder</b> to quickly get the clear, original version of any
        URL.
      </Typography>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Frequently Asked Questions (FAQs)
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🔍 What does URL decoding mean?
      </Typography>
      <Typography variant="body2" paragraph>
        URL decoding converts encoded characters (like %20 for spaces) back to
        their original characters, making URLs human-readable again.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🆓 Is the URL decoder free to use?
      </Typography>
      <Typography variant="body2" paragraph>
        Free plan users will have credits deducted per decoding action. Paid
        users can decode URLs without any credit limits.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        🛠️ What kind of URLs can I decode?
      </Typography>
      <Typography variant="body2" paragraph>
        You can decode any URL encoded with percent signs, including those with
        spaces, symbols, and non-ASCII characters.
      </Typography>
      <Typography variant="body1" fontWeight="bold">
        🔄 Can I encode URLs here too?
      </Typography>
      <Typography variant="body2" paragraph>
        Yes! Use our{" "}
        <a
          href="/url-encoder-online"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          URL Encoder tool
        </a>{" "}
        to convert normal URLs into encoded format.
      </Typography>

      <Typography variant="body1" fontWeight="bold">
        📊 Why is URL decoding useful?
      </Typography>
      <Typography variant="body2" paragraph>
        It helps you see the actual URL content, making it easier to debug,
        analyze, or share URLs without confusion caused by encoding.
      </Typography>

      {/* CTA */}
      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
        >
          Decode Your URL Now
        </Button>
      </Box>
    </Container>
  );
};

export default UrlDecoderContent;
